import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { CampaignPreset } from 'src/api/campaignPresets/types';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { getGroups } from 'src/modules/groups/slices';
import { BuilderContextValue } from '../context';
import { CampaignForm } from '../hooks/useCampaignFormState';
import { CampaignPolicy, resolvePolicy } from '../policies';
import {
  useCampaignConfigLoader,
  useCampaignForm,
  useCampaignSave,
  useContactCaptureOffer,
  useUnsavedChangesGuard,
} from '../hooks';
import { Campaign, CampaignConfigSchema, CampaignExternalType, CampaignStatus, SaveMode } from '../types';

/**
 * Business logic for the campaign builder. Start reading here.
 *
 * The builder runs in two phases and there is a controller for each, because a
 * hook cannot be called conditionally: the second phase needs a resolved config,
 * so it can only run once the first has produced one.
 *
 *   1. useDynamicCampaignBuilderViewController — resolves everything one builder
 *      open needs (config, campaign, preset, merge tags, keywords, initial name)
 *      and reports loading/error. See hooks/useCampaignConfigLoader.
 *
 *   2. useCampaignBuilderController — given that, picks the policy for the
 *      campaign type, builds the form around it and wires saving.
 *
 * Everything type-specific lives behind `policy`: which fields render
 * (fieldManifest), what the defaults are (getDefaults), what goes to the API
 * (toPayload), what makes the buttons clickable (isPrimaryActionDisabled /
 * isDraftActionDisabled). Adding a campaign type means adding a policy, not
 * editing this file. See policies/CampaignPolicy.
 */

interface CbnLocationState {
  duplicateCampaignId?: string;
  appointmentTypeId?: string;
  preselectedGroupId?: string;
}

export interface DynamicCampaignBuilderParams {
  /** Config UUID or campaign-type name from the route. */
  configIdOrType: string;
  /** See CampaignConfigLoaderParams.applicationId. */
  applicationId?: string;
  /** Saves in place when hosted in another builder. */
  onSaved?: (campaign: Campaign) => void;
  /** Present when editing a saved campaign. */
  campaignId?: string;
  /** Present when launching from a saved preset (?presetId). */
  presetId?: string;
}

/** Everything the second phase needs; produced by the first. */
export interface CampaignBuilderParams
  extends Pick<BuilderContextValue, 'config' | 'metadata' | 'keywords' | 'addKeyword'> {
  messageTemplates?: NonNullable<BuilderContextValue['messageTemplates']>;
  startWithEmptyMessages?: boolean;
  /** Fixes the saved external type when the host, not the config, decides it. */
  externalType?: CampaignExternalType;
  onSaved?: (campaign: Campaign) => void;
  configId: string | null;
  campaign: Campaign | null;
  campaignId?: string;
  isDuplicate: boolean;
  preset: CampaignPreset | null;
  /** Pre-resolved unique name for duplicate / preset flows; null otherwise. */
  initialName: string | null;
  preselectedAppointmentTypeId?: string;
  preselectedGroupId?: string;
  preselectedGroupName?: string;
}

export interface DynamicCampaignBuilderViewController {
  isPageLoading: boolean;
  error: string | null;
  /** Null until the config resolves. */
  builder: CampaignBuilderParams | null;
}

/**
 * Phase 1 — load. Turns route params into everything one builder open needs.
 */
export const useDynamicCampaignBuilderViewController = ({
  configIdOrType,
  applicationId,
  campaignId,
  presetId,
  onSaved,
}: DynamicCampaignBuilderParams): DynamicCampaignBuilderViewController => {
  const location = useLocation();
  // "Duplicate" hands the source id through router state rather than the URL, so
  // it has to be captured once — a later navigation would drop it.
  // An embedded builder ignores the host page's router state, which belongs to that page.
  const routeState = onSaved ? null : location.state as CbnLocationState | null;
  const [duplicateCampaignId] = useState<string | undefined>(routeState?.duplicateCampaignId);
  const [preselectedAppointmentTypeId] = useState<string | undefined>(routeState?.appointmentTypeId);
  const [preselectedGroupId] = useState<string | undefined>(routeState?.preselectedGroupId);
  const groups = useSelector(getGroups);
  const preselectedGroupName = preselectedGroupId
    ? groups?.items?.find(group => group.id === preselectedGroupId)?.name
    : undefined;

  const loaded = useCampaignConfigLoader({
    configIdOrType,
    applicationId,
    campaignId,
    duplicateCampaignId,
    presetId,
  });

  const {
    config, configId, campaign, isDuplicate, preset, metadata, messageTemplates, keywords, addKeyword,
  } = loaded;

  const builder = useMemo<CampaignBuilderParams | null>(
    () =>
      config
        ? {
            config,
            onSaved,
            configId,
            campaign: campaign ?? null,
            campaignId,
            isDuplicate,
            preset,
            metadata,
            messageTemplates: messageTemplates ?? [],
            keywords,
            addKeyword,
            initialName: loaded.initialName,
            preselectedAppointmentTypeId,
            preselectedGroupId,
            preselectedGroupName,
          }
        : null,
    [config, onSaved, configId, campaign, campaignId, isDuplicate, preset, metadata, messageTemplates,
      keywords, addKeyword, loaded.initialName, preselectedAppointmentTypeId,
      preselectedGroupId, preselectedGroupName],
  );

  return {
    // Strictly the in-flight flag. It used to also cover "no config", which meant
    // a failed load spun forever instead of showing its error — the loader now
    // guarantees an error whenever it finishes without a config.
    isPageLoading: loaded.loading,
    error: loaded.error,
    builder,
  };
};

export interface CampaignBuilderController {
  isSaving: boolean;
  policy: CampaignPolicy;
  form: CampaignForm;
  /** Value for BuilderProvider — config, merge tags, keywords, read-only state. */
  builderContext: BuilderContextValue;
  /** Heading text, verb included. Read-only campaigns are viewed, so they get no verb. */
  title: string;
  isCampaignReadOnly: boolean;
  isExistingCampaign: boolean;
  save: (status: CampaignStatus, mode: SaveMode, activate?: boolean) => Promise<unknown>;
  /** Banned-words modal state, raised when activation hits the blocklist. */
  blocklist: ReturnType<typeof useCampaignSave>['blocklist'];
  /** Props for ActivationGateDialog — onboarding / subscription gate. */
  activationGate: ReturnType<typeof useCampaignSave>['activationGate'];
  KeywordConfirmationDialog: ReturnType<typeof useCampaignSave>['KeywordConfirmationDialog'];
  navigationGuard: ReturnType<typeof useUnsavedChangesGuard>;
  /** Everything the action row needs to label and enable its buttons. */
  actions: {
    isEdit: boolean;
    campaignStatus?: CampaignStatus;
    isNewOrDuplicate: boolean;
    isDirty: boolean;
    primaryDisabled: boolean;
    draftDisabled: boolean;
  };
  headerActions: {
    config: CampaignConfigSchema;
    configId: string | null;
    campaign: Campaign | null;
    isExistingCampaign: boolean;
  };
}

/**
 * Phase 2 — edit and save. Everything below the loading gate.
 */
export const useCampaignBuilderController = ({
  config,
  configId,
  campaign,
  campaignId,
  isDuplicate,
  preset,
  metadata,
  messageTemplates = [],
  keywords,
  addKeyword,
  initialName,
  preselectedAppointmentTypeId,
  preselectedGroupId,
  preselectedGroupName,
  onSaved,
  startWithEmptyMessages,
  externalType,
}: CampaignBuilderParams): CampaignBuilderController => {
  const companyId = useSelector(getCurrentCompanyId) as string;
  // The one decision that drives everything type-specific.
  const policy = resolvePolicy(config);

  // A duplicate is a new campaign, so its source's status says nothing about activation.
  const contactCaptureOfferState = useContactCaptureOffer(
    config.type, isDuplicate ? undefined : campaign?.status,
  );
  const { form, submit, isDirty, isSaving } = useCampaignForm({
    onSaved,
    saveRedirectState: onSaved ? undefined : contactCaptureOfferState,
    startWithEmptyMessages,
    externalType,
    policy,
    config,
    configId,
    campaign,
    preset,
    forceCreate: isDuplicate,
    initialName,
    preselectedAppointmentTypeId,
    preselectedGroupId,
    preselectedGroupName,
  });
  const navigationGuard = useUnsavedChangesGuard(isDirty, { redirectBackToCampaigns: !onSaved });

  // A saved campaign the backend marked non-editable is viewed, not edited. A
  // duplicate is a brand-new draft, so it is never read-only even when the
  // source campaign was.
  const isExistingCampaign = !!campaignId && !isDuplicate;
  const isCampaignReadOnly = isExistingCampaign && campaign?.isEditable === false;

  const builderContext = useMemo<BuilderContextValue>(
    () => ({
      config,
      metadata,
      messageTemplates,
      keywords,
      addKeyword,
      isCampaignReadOnly,
      campaignId: isExistingCampaign ? campaignId : undefined,
      isDuplicate,
      campaignGroups: campaign?.groups ?? [],
    }),
    [config, metadata, messageTemplates, keywords, addKeyword, isCampaignReadOnly,
      isExistingCampaign, campaignId, isDuplicate, campaign?.groups],
  );

  // isPrimaryActionDisabled parses every message body to decide whether it is
  // empty, so this is the costly part of a render — keep it off the keystroke
  // path unless the values actually changed.
  const actions = useMemo(
    () => ({
      isEdit: isExistingCampaign,
      campaignStatus: campaign?.status,
      isNewOrDuplicate: !campaignId || isDuplicate,
      isDirty,
      primaryDisabled: policy.isPrimaryActionDisabled(form.values, config),
      draftDisabled: policy.isDraftActionDisabled(form.values, config),
    }),
    [isExistingCampaign, campaign?.status, campaignId, isDuplicate, isDirty,
      policy, config, form.values],
  );

  const { save, isChecking, blocklist, activationGate, KeywordConfirmationDialog } = useCampaignSave({
    policy,
    form,
    submit,
    campaign,
    isDuplicate,
    isExistingCampaign,
    companyId,
    keywords,
  });

  return {
    isSaving: isSaving || isChecking,
    policy,
    form,
    builderContext,
    title: `${isCampaignReadOnly ? '' : isExistingCampaign ? 'Update ' : 'Create '}${config.name}`,
    isCampaignReadOnly,
    isExistingCampaign,
    save,
    blocklist,
    activationGate,
    KeywordConfirmationDialog,
    navigationGuard,
    actions,
    headerActions: { config, configId, campaign, isExistingCampaign },
  };
};
